import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';
import { UIStyle } from './UIStyle';

export class ScoreHUD {
  private scene: Phaser.Scene;
  private container?: Phaser.GameObjects.Container;
  private bg?: Phaser.GameObjects.Graphics;
  private scoreText?: Phaser.GameObjects.Text;
  private popText?: Phaser.GameObjects.Text;
  private popBaseY = 0;
  private unbindResize?: () => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(initialScore = 0): void {
    this.container = this.scene.add.container(0, 0);

    this.bg = this.scene.add.graphics();
    this.scoreText = this.scene.add.text(0, 0, '', UIStyle.onDarkPanel(UIStyle.px(18), '#ffffff', true));
    this.popText = this.scene.add.text(0, 0, '', UIStyle.onDarkPanel(UIStyle.px(17), UIStyle.accentColor(), true));

    this.container.add([this.bg, this.scoreText, this.popText]);
    this.container.setDepth(50);
    this.container.setScrollFactor(0);

    this.updateScore(initialScore);

    const onResize = () => this.relayout();
    window.addEventListener('resize', onResize);
    const unbindResponsive = ResponsiveSystem.onResize(() => this.relayout());
    this.unbindResize = () => {
      window.removeEventListener('resize', onResize);
      unbindResponsive();
    };
  }

  private measureLayout(scoreLabel: string, isMobile: boolean) {
    const base = ResponsiveSystem.getScoreHUDBase(this.scene.game, isMobile);
    const fontSize = isMobile ? UIStyle.px(15) : UIStyle.px(17);
    const style = UIStyle.onDarkPanel(fontSize, '#ffffff', true);

    const probe = this.scene.add.text(0, 0, scoreLabel, style);
    const textW = probe.width;
    probe.destroy();

    const panelW = Math.ceil(
      base.leftPad + base.iconSize + base.textGap + textW + base.rightPad,
    );
    const fsReserve = ResponsiveSystem.getFullscreenReservedWidth(this.scene.game);
    const anchorX = base.safeRight - fsReserve;

    return { ...base, panelW, anchorX, fontSize, style };
  }

  private redrawPanel(
    layout: ReturnType<ScoreHUD['measureLayout']>,
    icon: Phaser.GameObjects.Image,
  ): void {
    if (!this.bg) return;

    const { panelW, panelH, leftPad, iconSize } = layout;
    const radius = panelH / 2;

    this.bg.clear();
    this.bg.fillStyle(0x102235, 1);
    this.bg.fillRoundedRect(-panelW, 0, panelW, panelH, radius);
    this.bg.fillStyle(0x1a3048, 1);
    this.bg.fillRoundedRect(-panelW + 1, 1, panelW - 2, panelH - 2, radius - 1);
    this.bg.lineStyle(2, 0xd4a373, 0.8);
    this.bg.strokeRoundedRect(-panelW, 0, panelW, panelH, radius);

    const iconX = -panelW + leftPad;
    icon.setPosition(iconX, panelH / 2);
    icon.setDisplaySize(iconSize, iconSize);
  }

  private relayout(): void {
    if (!this.container || !this.scoreText || !this.bg) return;

    const isMobile = ResponsiveSystem.isMobile(this.scene);
    const scoreLabel = this.scoreText.text;
    const layout = this.measureLayout(scoreLabel, isMobile);

    let icon = this.container.list.find(
      (obj) => obj instanceof Phaser.GameObjects.Image,
    ) as Phaser.GameObjects.Image | undefined;

    if (!icon) {
      icon = this.scene.add.image(0, 0, TEXTURE_KEYS.ICON_RAFFLESIA);
      icon.setOrigin(0, 0.5);
      this.container.addAt(icon, 1);
    }

    this.redrawPanel(layout, icon);

    const textX = -layout.panelW + layout.leftPad + layout.iconSize + layout.textGap;
    this.scoreText.setStyle(layout.style);
    this.scoreText.setPosition(textX, layout.panelH / 2 + 1);
    this.scoreText.setOrigin(0, 0.5);

    this.popBaseY = layout.panelH / 2;
    this.popText?.setPosition(-layout.rightPad, this.popBaseY);
    this.popText?.setOrigin(1, 0.5);

    this.container.setPosition(layout.anchorX, layout.anchorY);
    ResponsiveSystem.setScoreHudBounds(layout.anchorX, layout.panelH, layout.anchorY);
    ResponsiveSystem.syncFullscreenTogglePosition(this.scene.game);
  }

  updateScore(points: number): void {
    if (!this.scoreText) return;
    this.scoreText.setText(`Rafflesia Point: ${points}`);
    this.relayout();
  }

  showDelta(delta: number): void {
    if (!this.popText) return;
    const sign = delta >= 0 ? '+' : '';
    const color = delta === 0 ? '#b0b0b0' : UIStyle.accentColor();
    this.popText.setColor(color);
    this.popText.setText(`${sign}${delta}`);
    this.popText.setAlpha(1);
    this.popText.setY(this.popBaseY);

    this.scene.tweens.add({
      targets: this.popText,
      y: this.popBaseY - 10,
      alpha: 0,
      duration: 900,
      ease: 'Cubic.easeOut',
    });

    this.scene.tweens.add({
      targets: this.scoreText,
      scaleX: 1.08,
      scaleY: 1.08,
      duration: 120,
      yoyo: true,
    });
  }

  setVisible(visible: boolean): void {
    this.container?.setVisible(visible);
  }

  destroy(): void {
    ResponsiveSystem.clearScoreHudBounds();
    ResponsiveSystem.syncFullscreenTogglePosition(this.scene.game);
    this.unbindResize?.();
    this.unbindResize = undefined;
    this.container?.destroy();
    this.container = undefined;
  }
}
